import React, { useState, useEffect, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import AdminHeader from './AdminHeader';
import {
  getAllSavedContentUnpaged,
  updateLayout,
  updateCategoryLayout,
  updateArticleCategory,
  deleteContent,
  toggleHideContent,
} from '../services/api';

// ── Constants ─────────────────────────────────────────────────────────────────

const HOME_SECTIONS = [
  { id: 'featured',       label: 'Featured Stories', icon: '⭐', color: '#1565C0', description: '1st = Hero · 2nd–5th = Top Stories' },
  { id: 'more_headlines', label: 'More Headlines',   icon: '📌', color: '#6A1B9A', description: 'Sidebar (up to 5 shown)' },
  { id: 'latest_news',    label: 'Latest News',      icon: '📰', color: '#2E7D32', description: 'Main news grid' },
  { id: 'bottom',         label: 'Other Stories',    icon: '📋', color: '#E65100', description: 'Bottom list' },
];

const ARTICLE_CATEGORIES = [
  { value: 'longevity',     label: 'Longevity',     icon: '🧬', color: '#1b5e20' },
  { value: 'nutrition',     label: 'Nutrition',     icon: '🥗', color: '#1565c0' },
  { value: 'research',      label: 'Research',      icon: '🔬', color: '#4a148c' },
  { value: 'mental-health', label: 'Mental Health', icon: '🧠', color: '#4e342e' },
  { value: 'prevention',    label: 'Prevention',    icon: '🏃', color: '#bf360c' },
];

const BACKEND_BASE_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/api', '')
  : 'http://localhost:5000';

// ── Helpers ───────────────────────────────────────────────────────────────────

function getImageUrl(p) {
  if (!p) return null;
  if (p.startsWith('http://') || p.startsWith('https://')) return p;
  return `${BACKEND_BASE_URL}${p.startsWith('/') ? p : '/' + p}`;
}

function extractFirstImage(html, saved) {
  if (saved) return getImageUrl(saved);
  if (!html) return null;
  const m = html.match(/<img[^>]+src=["']([^"']+)["']/i);
  return m ? m[1] : null;
}

function fmtDate(d) {
  const ms = Date.now() - new Date(d);
  const h = Math.floor(ms / 3600000), day = Math.floor(ms / 86400000);
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m ago`;
  if (h < 24) return `${h}h ago`;
  if (day < 7) return `${day}d ago`;
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function autoAssignSections(articles) {
  const hasCustom = articles.some(a => a.sortOrder !== 9999 || a.section !== 'latest_news');
  if (hasCustom) return articles;
  return articles.map((a, i) => {
    if (i === 0) return { ...a, section: 'featured', sortOrder: 0 };
    if (i < 5)  return { ...a, section: 'featured', sortOrder: i };
    if (i < 10) return { ...a, section: 'more_headlines', sortOrder: i - 5 };
    if (i < 22) return { ...a, section: 'latest_news', sortOrder: i - 10 };
    return { ...a, section: 'bottom', sortOrder: i - 22 };
  });
}

function groupBySections(articles) {
  const g = { featured: [], more_headlines: [], latest_news: [], bottom: [] };
  articles.forEach(a => {
    const s = g[a.section] !== undefined ? a.section : 'latest_news';
    g[s].push({ ...a });
  });
  Object.keys(g).forEach(k => g[k].sort((a, b) => a.sortOrder - b.sortOrder));
  return g;
}

function groupByCategories(articles) {
  const g = {};
  ARTICLE_CATEGORIES.forEach(c => { g[c.value] = []; });
  g.uncategorized = [];
  articles.forEach(a => {
    const key = a.category && g[a.category] !== undefined ? a.category : 'uncategorized';
    g[key].push({ ...a });
  });
  ARTICLE_CATEGORIES.forEach(c => {
    g[c.value].sort((a, b) => (a.categorySortOrder ?? 9999) - (b.categorySortOrder ?? 9999));
  });
  return g;
}

// ── Section badge (used in category tab cards) ────────────────────────────────

function SectionBadge({ section }) {
  const s = HOME_SECTIONS.find(x => x.id === section);
  if (!s) return null;
  return (
    <span style={{ background: s.color + '22', color: s.color, border: `1px solid ${s.color}44`, padding: '1px 7px', borderRadius: '4px', fontSize: '0.68em', fontWeight: 700 }}>
      {s.icon} {s.label}
    </span>
  );
}

// ── Article Card ──────────────────────────────────────────────────────────────

function ArticleCard({ article, index, isFeaturedHero, onDelete, onHide, onMove, onChangeCategory, showSectionBadge }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [submenu, setSubmenu] = useState(null); // null | 'section' | 'category'
  const menuRef = useRef(null);
  const imgUrl = extractFirstImage(article.htmlContent, article.imageUrl);
  const catInfo = ARTICLE_CATEGORIES.find(c => c.value === article.category);

  useEffect(() => {
    if (!menuOpen) return;
    const h = e => { if (menuRef.current && !menuRef.current.contains(e.target)) { setMenuOpen(false); setSubmenu(null); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menuOpen]);

  return (
    <Draggable draggableId={String(article.id)} index={index}>
      {(provided, snapshot) => (
        <div ref={provided.innerRef} {...provided.draggableProps} style={{ ...provided.draggableProps.style, marginBottom: '6px' }}>
          <div style={{
            position: 'relative',
            background: snapshot.isDragging ? '#e8f4fd' : isFeaturedHero ? '#fffde7' : '#fff',
            border: `1px solid ${snapshot.isDragging ? '#2196F3' : isFeaturedHero ? '#f9a825' : '#e0e0e0'}`,
            borderRadius: '8px', padding: '10px 10px 10px 4px',
            display: 'flex', gap: '8px', alignItems: 'flex-start',
            boxShadow: snapshot.isDragging ? '0 8px 24px rgba(33,150,243,0.25)' : '0 1px 3px rgba(0,0,0,0.06)',
            opacity: article.isHidden ? 0.6 : 1,
          }}>
            {/* Drag handle */}
            <div {...provided.dragHandleProps} style={{ padding: '4px 3px', color: '#c0c0c0', cursor: 'grab', flexShrink: 0, fontSize: '16px', marginTop: '2px', userSelect: 'none' }} title="Drag to reorder">⠿</div>

            {/* Hero badge */}
            {isFeaturedHero && (
              <div style={{ position: 'absolute', top: '-10px', left: '10px', background: '#f9a825', color: '#fff', fontSize: '0.68em', fontWeight: 800, padding: '2px 8px', borderRadius: '4px', letterSpacing: '0.5px' }}>HERO</div>
            )}

            {/* Thumbnail */}
            {imgUrl && (
              <div style={{ flexShrink: 0, width: '64px', height: '48px', borderRadius: '5px', overflow: 'hidden', background: '#f0f0f0' }}>
                <img src={imgUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.parentElement.style.display = 'none'; }} />
              </div>
            )}

            {/* Text + badges */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.84em', fontWeight: 600, color: '#1a1a1a', lineHeight: 1.35, marginBottom: '4px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                {article.isHidden && <span style={{ background: '#ff9800', color: '#fff', fontSize: '0.7em', padding: '1px 5px', borderRadius: '3px', marginRight: '5px', fontWeight: 700 }}>HIDDEN</span>}
                {article.title}
              </div>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.71em', color: '#999' }}>{fmtDate(article.createdAt)}</span>
                {showSectionBadge && <SectionBadge section={article.section} />}
                {catInfo && !showSectionBadge && (
                  <span style={{ background: catInfo.color + '22', color: catInfo.color, border: `1px solid ${catInfo.color}44`, padding: '1px 7px', borderRadius: '4px', fontSize: '0.68em', fontWeight: 700 }}>
                    {catInfo.icon} {catInfo.label}
                  </span>
                )}
              </div>
            </div>

            {/* ⋮ Menu */}
            <div ref={menuRef} style={{ position: 'relative', flexShrink: 0 }}>
              <button onClick={e => { e.stopPropagation(); setMenuOpen(!menuOpen); setSubmenu(null); }}
                style={{ background: menuOpen ? '#f0f0f0' : 'transparent', border: '1px solid transparent', borderRadius: '4px', padding: '3px 7px', cursor: 'pointer', fontSize: '18px', color: '#555', lineHeight: 1 }}>
                ⋮
              </button>

              {menuOpen && (
                <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', background: '#fff', border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 6px 24px rgba(0,0,0,0.15)', zIndex: 9999, minWidth: '190px', overflow: 'hidden', fontSize: '0.88em' }}>

                  {/* Homepage Section submenu */}
                  <div onMouseEnter={e => { e.currentTarget.style.background = '#f5f5f5'; setSubmenu('section'); }} onMouseLeave={e => { e.currentTarget.style.background = ''; }}
                    style={{ padding: '10px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0', color: '#333' }}>
                    <span>📦 Homepage Section…</span><span style={{ color: '#aaa', fontSize: '0.9em' }}>▶</span>
                  </div>
                  {submenu === 'section' && (
                    <div style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                      {HOME_SECTIONS.map(sec => {
                        const cur = article.section === sec.id;
                        return (
                          <div key={sec.id}
                            onClick={() => { if (!cur) onMove(article.id, sec.id); setMenuOpen(false); setSubmenu(null); }}
                            onMouseEnter={e => { e.currentTarget.style.background = cur ? '' : '#e3f2fd'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = ''; }}
                            style={{ padding: '8px 14px 8px 26px', cursor: cur ? 'default' : 'pointer', color: cur ? '#1976D2' : '#444', fontWeight: cur ? 700 : 400, display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {cur ? '✓ ' : ''}{sec.icon} {sec.label}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Change Category submenu */}
                  <div onMouseEnter={e => { e.currentTarget.style.background = '#f5f5f5'; setSubmenu('category'); }} onMouseLeave={e => { e.currentTarget.style.background = ''; }}
                    style={{ padding: '10px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0', color: '#333' }}>
                    <span>🏷️ Change Category…</span><span style={{ color: '#aaa', fontSize: '0.9em' }}>▶</span>
                  </div>
                  {submenu === 'category' && (
                    <div style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                      <div
                        onClick={() => { if (article.category) onChangeCategory(article.id, null); setMenuOpen(false); setSubmenu(null); }}
                        onMouseEnter={e => { e.currentTarget.style.background = article.category ? '#fff8e1' : ''; }}
                        onMouseLeave={e => { e.currentTarget.style.background = ''; }}
                        style={{ padding: '8px 14px 8px 26px', cursor: article.category ? 'pointer' : 'default', color: article.category ? '#e65100' : '#bbb', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {!article.category ? '✓ ' : ''}❌ No Category (Unassign)
                      </div>
                      {ARTICLE_CATEGORIES.map(cat => {
                        const cur = article.category === cat.value;
                        return (
                          <div key={cat.value}
                            onClick={() => { if (!cur) onChangeCategory(article.id, cat.value); setMenuOpen(false); setSubmenu(null); }}
                            onMouseEnter={e => { e.currentTarget.style.background = cur ? '' : '#e3f2fd'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = ''; }}
                            style={{ padding: '8px 14px 8px 26px', cursor: cur ? 'default' : 'pointer', color: cur ? cat.color : '#444', fontWeight: cur ? 700 : 400, display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {cur ? '✓ ' : ''}{cat.icon} {cat.label}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Hide */}
                  <div onClick={() => { onHide(article.id); setMenuOpen(false); }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f5f5f5'; }} onMouseLeave={e => { e.currentTarget.style.background = ''; }}
                    style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', color: '#333' }}>
                    {article.isHidden ? '👁 Unhide Article' : '🔒 Hide Article'}
                  </div>

                  {/* Delete */}
                  <div onClick={() => { onDelete(article.id); setMenuOpen(false); }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#ffebee'; }} onMouseLeave={e => { e.currentTarget.style.background = ''; }}
                    style={{ padding: '10px 14px', cursor: 'pointer', color: '#c62828' }}>
                    🗑 Delete Article
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
}

// ── Section Column (Home Tab) ──────────────────────────────────────────────────

function SectionColumn({ section, articles, onDelete, onHide, onMove, onChangeCategory }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: section.color, color: '#fff', padding: '10px 14px', borderRadius: '8px 8px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '0.9em' }}>{section.icon} {section.label}</div>
          <div style={{ fontSize: '0.72em', opacity: 0.85, marginTop: '2px' }}>{section.description}</div>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.25)', borderRadius: '20px', padding: '3px 12px', fontWeight: 800, fontSize: '0.85em', minWidth: '28px', textAlign: 'center' }}>
          {articles.length}
        </div>
      </div>
      <Droppable droppableId={section.id}>
        {(provided, snapshot) => (
          <div ref={provided.innerRef} {...provided.droppableProps}
            style={{ minHeight: '100px', padding: '8px', background: snapshot.isDraggingOver ? '#e3f2fd' : '#f7f8fa', border: `2px ${snapshot.isDraggingOver ? 'dashed #2196F3' : 'solid #e0e0e0'}`, borderTop: 'none', borderRadius: '0 0 8px 8px', transition: 'background 0.12s' }}>
            {articles.length === 0 && (
              <div style={{ textAlign: 'center', padding: '24px 10px', color: '#bbb', fontSize: '0.82em', fontStyle: 'italic' }}>Drop articles here</div>
            )}
            {articles.map((article, index) => (
              <ArticleCard key={article.id} article={article} index={index}
                isFeaturedHero={section.id === 'featured' && index === 0}
                onDelete={onDelete} onHide={onHide} onMove={onMove} onChangeCategory={onChangeCategory} />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
}

// ── Category Tab ───────────────────────────────────────────────────────────────

function CategoryTabContent({ catKey, articles, onDelete, onHide, onMove, onChangeCategory }) {
  const isUncategorized = catKey === 'uncategorized';
  const catInfo = isUncategorized
    ? { label: 'Uncategorized', icon: '❓', color: '#757575' }
    : ARTICLE_CATEGORIES.find(c => c.value === catKey);

  return (
    <div>
      {/* Category banner */}
      <div style={{ background: catInfo.color, color: '#fff', padding: '22px 24px', borderRadius: '10px', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '16px' }}>
        <span style={{ fontSize: '2.2em' }}>{catInfo.icon}</span>
        <div>
          <div style={{ fontWeight: 800, fontSize: '1.2em' }}>{catInfo.label}</div>
          <div style={{ fontSize: '0.83em', opacity: 0.8, marginTop: '3px' }}>
            {articles.length} article{articles.length !== 1 ? 's' : ''}
            {isUncategorized
              ? ' — assign a category using the ⋮ menu on each article'
              : ' · drag to reorder · first article is the hero on the category page'}
          </div>
        </div>
      </div>

      {articles.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '64px 20px', background: '#fff', borderRadius: '10px', border: '2px dashed #e0e0e0', color: '#aaa' }}>
          <div style={{ fontSize: '3em', marginBottom: '14px' }}>📭</div>
          <p style={{ fontSize: '0.95em', marginBottom: '6px' }}>No articles here yet.</p>
          <p style={{ fontSize: '0.83em', color: '#bbb' }}>Use the ⋮ menu on any article to assign it to this category.</p>
        </div>
      ) : (
        <Droppable droppableId={`cat-${catKey}`} isDropDisabled={isUncategorized}>
          {(provided, snapshot) => (
            <div ref={provided.innerRef} {...provided.droppableProps}
              style={{ background: snapshot.isDraggingOver ? '#e3f2fd' : 'transparent', border: snapshot.isDraggingOver ? '2px dashed #2196F3' : '2px solid transparent', borderRadius: '8px', padding: snapshot.isDraggingOver ? '4px' : '0', minHeight: '60px' }}>
              {articles.map((article, index) => (
                <div key={article.id} style={{ display: 'flex', alignItems: 'stretch', gap: '10px', marginBottom: '6px' }}>
                  {/* Position badge */}
                  {!isUncategorized && (
                    <div style={{ width: '42px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: index === 0 ? catInfo.color : '#f0f0f0', color: index === 0 ? '#fff' : '#888', borderRadius: '7px', fontWeight: 800, fontSize: index === 0 ? '1.1em' : '0.95em' }}>
                      {index === 0 ? '★' : index + 1}
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <ArticleCard article={article} index={index} isFeaturedHero={false}
                      onDelete={onDelete} onHide={onHide} onMove={onMove} onChangeCategory={onChangeCategory}
                      showSectionBadge={true} />
                  </div>
                </div>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function AdminPreviewPage() {
  const [allArticles, setAllArticles]       = useState([]);
  const [sections, setSections]             = useState({ featured: [], more_headlines: [], latest_news: [], bottom: [] });
  const [categoryOrders, setCategoryOrders] = useState({});
  const [activeTab, setActiveTab]           = useState('home');
  const [loading, setLoading]               = useState(true);
  const [saving, setSaving]                 = useState(false);
  const [homeHasChanges, setHomeHasChanges] = useState(false);
  const [dirtyCategories, setDirtyCategories] = useState(new Set());
  const [status, setStatus]                 = useState({ type: '', message: '' });

  useEffect(() => { loadContent(); }, []);

  const loadContent = async () => {
    try {
      setLoading(true);
      const res = await getAllSavedContentUnpaged();
      if (res.success) {
        const assigned = autoAssignSections(res.data);
        setAllArticles(assigned);
        setSections(groupBySections(assigned));
        setCategoryOrders(groupByCategories(assigned));
      }
    } catch { showStatus('error', 'Failed to load articles'); }
    finally { setLoading(false); }
  };

  // ── State helpers ──────────────────────────────────────────────────────────

  const patchArticle = (id, updates) => {
    setAllArticles(prev => prev.map(a => a.id === id ? { ...a, ...updates } : a));
    setSections(prev => {
      const n = {};
      Object.keys(prev).forEach(s => { n[s] = prev[s].map(a => a.id === id ? { ...a, ...updates } : a); });
      return n;
    });
    setCategoryOrders(prev => {
      const n = {};
      Object.keys(prev).forEach(k => { n[k] = prev[k].map(a => a.id === id ? { ...a, ...updates } : a); });
      return n;
    });
  };

  const removeFromAll = (id) => {
    setAllArticles(prev => prev.filter(a => a.id !== id));
    setSections(prev => { const n = {}; Object.keys(prev).forEach(s => { n[s] = prev[s].filter(a => a.id !== id); }); return n; });
    setCategoryOrders(prev => { const n = {}; Object.keys(prev).forEach(k => { n[k] = prev[k].filter(a => a.id !== id); }); return n; });
  };

  // ── Drag & Drop ────────────────────────────────────────────────────────────

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    if (activeTab === 'home') {
      if (source.droppableId === destination.droppableId && source.index === destination.index) return;
      const ns = { featured: [...sections.featured], more_headlines: [...sections.more_headlines], latest_news: [...sections.latest_news], bottom: [...sections.bottom] };
      const [moved] = ns[source.droppableId].splice(source.index, 1);
      moved.section = destination.droppableId;
      ns[destination.droppableId].splice(destination.index, 0, moved);
      Object.keys(ns).forEach(k => { ns[k] = ns[k].map((a, i) => ({ ...a, sortOrder: i })); });
      setSections(ns);
      setHomeHasChanges(true);
    } else {
      const catKey = activeTab;
      if (source.index === destination.index) return;
      const items = [...(categoryOrders[catKey] || [])];
      const [moved] = items.splice(source.index, 1);
      items.splice(destination.index, 0, moved);
      const updated = items.map((a, i) => ({ ...a, categorySortOrder: i }));
      setCategoryOrders(prev => ({ ...prev, [catKey]: updated }));
      setDirtyCategories(prev => new Set([...prev, catKey]));
    }
  };

  // ── Actions ────────────────────────────────────────────────────────────────

  const handleMove = (articleId, targetSection) => {
    const ns = { featured: [...sections.featured], more_headlines: [...sections.more_headlines], latest_news: [...sections.latest_news], bottom: [...sections.bottom] };
    let moved = null;
    for (const sec of Object.keys(ns)) {
      const idx = ns[sec].findIndex(a => a.id === articleId);
      if (idx !== -1) { [moved] = ns[sec].splice(idx, 1); ns[sec] = ns[sec].map((a, i) => ({ ...a, sortOrder: i })); break; }
    }
    if (moved) {
      moved = { ...moved, section: targetSection, sortOrder: ns[targetSection].length };
      ns[targetSection] = [...ns[targetSection], moved];
      setCategoryOrders(prev => { const n = {}; Object.keys(prev).forEach(k => { n[k] = prev[k].map(a => a.id === articleId ? { ...a, section: targetSection } : a); }); return n; });
    }
    setSections(ns);
    setHomeHasChanges(true);
    const sec = HOME_SECTIONS.find(s => s.id === targetSection);
    showStatus('success', `Moved to "${sec?.label || targetSection}"`);
  };

  const handleChangeCategory = async (articleId, newCategory) => {
    const article = allArticles.find(a => a.id === articleId);
    if (!article) return;
    const oldKey = article.category || 'uncategorized';
    const newKey = newCategory || 'uncategorized';
    try {
      const res = await updateArticleCategory(articleId, newCategory);
      if (!res.success) { showStatus('error', 'Failed to update category'); return; }
      patchArticle(articleId, { category: newCategory || null });
      setCategoryOrders(prev => {
        const n = { ...prev };
        n[oldKey] = (n[oldKey] || []).filter(a => a.id !== articleId);
        const updated = { ...article, category: newCategory || null };
        n[newKey] = [...(n[newKey] || []), updated];
        return n;
      });
      const catLabel = ARTICLE_CATEGORIES.find(c => c.value === newCategory)?.label || 'Uncategorized';
      showStatus('success', `✅ Category changed to ${catLabel}`);
    } catch { showStatus('error', 'Failed to change category'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this article permanently? This cannot be undone.')) return;
    try {
      const res = await deleteContent(id);
      if (res.success) { removeFromAll(id); showStatus('success', '🗑 Article deleted'); }
    } catch { showStatus('error', 'Failed to delete'); }
  };

  const handleHide = async (id) => {
    try {
      const res = await toggleHideContent(id);
      if (res.success) {
        const cur = allArticles.find(a => a.id === id);
        patchArticle(id, { isHidden: !cur?.isHidden });
        showStatus('success', res.message);
      }
    } catch { showStatus('error', 'Failed to toggle visibility'); }
  };

  const handleSaveHomeLayout = async () => {
    try {
      setSaving(true);
      const layout = [];
      Object.keys(sections).forEach(sec => { sections[sec].forEach((a, i) => layout.push({ id: a.id, section: sec, sortOrder: i })); });
      const res = await updateLayout(layout);
      if (res.success) { setHomeHasChanges(false); showStatus('success', '✅ Home layout saved — live on public site'); }
    } catch { showStatus('error', 'Failed to save layout'); }
    finally { setSaving(false); }
  };

  const handleSaveCategoryLayout = async (catKey) => {
    try {
      setSaving(true);
      const layout = (categoryOrders[catKey] || []).map((a, i) => ({ id: a.id, categorySortOrder: i }));
      const res = await updateCategoryLayout(layout);
      if (res.success) {
        setDirtyCategories(prev => { const s = new Set(prev); s.delete(catKey); return s; });
        showStatus('success', '✅ Category order saved');
      }
    } catch { showStatus('error', 'Failed to save category order'); }
    finally { setSaving(false); }
  };

  const showStatus = (type, message) => {
    setStatus({ type, message });
    if (type === 'success') setTimeout(() => setStatus({ type: '', message: '' }), 2500);
  };

  // ── Derived ────────────────────────────────────────────────────────────────

  const isHomeTab = activeTab === 'home';
  const currentCatDirty = !isHomeTab && dirtyCategories.has(activeTab);

  const TABS = [
    { id: 'home', label: '🏠 Home Layout', color: '#1565C0', count: allArticles.length },
    ...ARTICLE_CATEGORIES.map(c => ({ id: c.value, label: `${c.icon} ${c.label}`, color: c.color, count: (categoryOrders[c.value] || []).length })),
    { id: 'uncategorized', label: '❓ Uncategorized', color: '#757575', count: (categoryOrders.uncategorized || []).length },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <AdminHeader title="Layout Editor" subtitle="Manage homepage layout · organize articles by category · drag to reorder" />

      {/* Status bar */}
      {status.message && (
        <div style={{ padding: '10px 20px', background: status.type === 'success' ? '#e8f5e9' : '#ffebee', color: status.type === 'success' ? '#2e7d32' : '#c62828', borderBottom: `1px solid ${status.type === 'success' ? '#a5d6a7' : '#ef9a9a'}`, fontSize: '0.9em', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {status.message}
          <button onClick={() => setStatus({ type: '', message: '' })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', fontSize: '16px' }}>✕</button>
        </div>
      )}

      {/* Tab Bar */}
      <div style={{ background: '#fff', borderBottom: '2px solid #e0e0e0', overflowX: 'auto', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', padding: '0 16px', minWidth: 'max-content' }}>
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{ padding: '14px 18px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.88em', fontWeight: activeTab === tab.id ? 700 : 500, color: activeTab === tab.id ? tab.color : '#666', borderBottom: activeTab === tab.id ? `3px solid ${tab.color}` : '3px solid transparent', marginBottom: '-2px', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
              {tab.label}
              {tab.count > 0 && (
                <span style={{ background: activeTab === tab.id ? tab.color : '#e0e0e0', color: activeTab === tab.id ? '#fff' : '#777', borderRadius: '10px', padding: '1px 7px', fontSize: '0.78em', fontWeight: 700 }}>{tab.count}</span>
              )}
              {tab.id !== 'home' && dirtyCategories.has(tab.id) && (
                <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#e65100', display: 'inline-block', flexShrink: 0 }} title="Unsaved changes" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e0e0e0', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          {isHomeTab && homeHasChanges && <span style={{ background: '#fff8e1', color: '#e65100', padding: '3px 10px', borderRadius: '12px', fontSize: '0.8em', fontWeight: 700 }}>● Unsaved changes</span>}
          {!isHomeTab && currentCatDirty && <span style={{ background: '#fff8e1', color: '#e65100', padding: '3px 10px', borderRadius: '12px', fontSize: '0.8em', fontWeight: 700 }}>● Unsaved order changes</span>}
          <span style={{ fontSize: '0.78em', color: '#aaa' }}>⠿ drag to reorder · ⋮ for more options</span>
        </div>
        {isHomeTab ? (
          <button onClick={handleSaveHomeLayout} disabled={saving || !homeHasChanges}
            style={{ background: homeHasChanges ? '#1976D2' : '#bdbdbd', color: '#fff', border: 'none', borderRadius: '7px', padding: '10px 22px', cursor: homeHasChanges ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: '0.9em', boxShadow: homeHasChanges ? '0 2px 8px rgba(25,118,210,0.35)' : 'none' }}>
            {saving ? '⏳ Saving…' : '💾 Save Home Layout'}
          </button>
        ) : activeTab !== 'uncategorized' ? (
          <button onClick={() => handleSaveCategoryLayout(activeTab)} disabled={saving || !currentCatDirty}
            style={{ background: currentCatDirty ? '#1976D2' : '#bdbdbd', color: '#fff', border: 'none', borderRadius: '7px', padding: '10px 22px', cursor: currentCatDirty ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: '0.9em', boxShadow: currentCatDirty ? '0 2px 8px rgba(25,118,210,0.35)' : 'none' }}>
            {saving ? '⏳ Saving…' : '💾 Save Category Order'}
          </button>
        ) : null}
      </div>

      {/* Content */}
      <div style={{ padding: '20px', margin: '0 auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px 20px', color: '#888' }}>
            <div style={{ fontSize: '2.5em', marginBottom: '12px' }}>⏳</div>
            <p>Loading articles…</p>
          </div>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            {/* ── Home Tab ─────────────────────────────────────────────── */}
            {isHomeTab && (
              <>
                {/* Section legend */}
                <div style={{ background: '#fff', borderRadius: '8px', padding: '10px 16px', marginBottom: '16px', display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap', border: '1px solid #e0e0e0' }}>
                  <span style={{ fontSize: '0.77em', color: '#999', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Homepage sections:</span>
                  {HOME_SECTIONS.map(sec => (
                    <div key={sec.id} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: sec.color, flexShrink: 0 }} />
                      <span style={{ fontSize: '0.8em', color: '#555' }}><strong>{sec.label}</strong><span style={{ color: '#aaa' }}> — {sec.description}</span></span>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '16px', alignItems: 'start' }}>
                  <SectionColumn section={HOME_SECTIONS[0]} articles={sections.featured} onDelete={handleDelete} onHide={handleHide} onMove={handleMove} onChangeCategory={handleChangeCategory} />
                  <SectionColumn section={HOME_SECTIONS[1]} articles={sections.more_headlines} onDelete={handleDelete} onHide={handleHide} onMove={handleMove} onChangeCategory={handleChangeCategory} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <SectionColumn section={HOME_SECTIONS[2]} articles={sections.latest_news} onDelete={handleDelete} onHide={handleHide} onMove={handleMove} onChangeCategory={handleChangeCategory} />
                </div>
                <div>
                  <SectionColumn section={HOME_SECTIONS[3]} articles={sections.bottom} onDelete={handleDelete} onHide={handleHide} onMove={handleMove} onChangeCategory={handleChangeCategory} />
                </div>
              </>
            )}

            {/* ── Category Tabs ─────────────────────────────────────────── */}
            {!isHomeTab && (
              <CategoryTabContent
                catKey={activeTab}
                articles={categoryOrders[activeTab] || []}
                onDelete={handleDelete}
                onHide={handleHide}
                onMove={handleMove}
                onChangeCategory={handleChangeCategory}
              />
            )}
          </DragDropContext>
        )}
      </div>
    </div>
  );
}
